import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaArrowLeft, FaPlay, FaPause, FaVolumeUp, FaCheck, FaTimes, FaStar, FaTrophy, FaFire, FaHeart } from 'react-icons/fa';
import FillBlanksExercise from '../../components/practice/FillBlanksExercise';
import ChallengeFillBlanksExercise from '../../components/practice/ChallengeFillBlanksExercise';
import ListenWriteExercise from '../../components/practice/ListenWriteExercise';
import VerbConjugationExercise from '../../components/practice/VerbConjugationExercise';
import VocabularyQuizExercise from '../../components/practice/VocabularyQuizExercise';
import { createChallengeSession } from '../../lib/challenges';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: 'english' | 'spanish' | 'french';
  song_lyrics: string;
  translation: string | null;
  song_vocabulary: any;
}

type ExerciseType = 'fill-blanks' | 'listen-write' | 'verb-conjugation' | 'vocabulary';

export default function SongPractice() {
  const [song, setSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseType>('fill-blanks');
  const [exerciseResults, setExerciseResults] = useState<{[key: string]: any}>({});
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [stat, setStat] = useState<any>(null);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Challenge mode state from navigation
  const challengeState = location.state as any;
  const isChallengeMode = challengeState?.challengeMode || false;
  const sessionId = challengeState?.sessionId;
  const expiresAt = challengeState?.expiresAt;
  const rewardPoints = challengeState?.rewardPoints || 100;
  const shouldCreateChallenge = challengeState?.createChallenge || false;

  useEffect(() => {
    loadSong();
    loadUserStats();
    
    // Auto-create challenge if requested
    if (shouldCreateChallenge && song) {
      handleCreateChallenge();
    }
  }, [id]);

  const handleCreateChallenge = async () => {
    if (!user || !song) return;
    
    try {
      const session = await createChallengeSession(song.id, 10, 15, 100);
      toast.success('¡Desafío creado! Otros pueden unirse ahora.');
      
      // Update state to challenge mode
      navigate(`/practice/song/${song.id}`, {
        state: {
          challengeMode: true,
          sessionId: session.id,
          expiresAt: session.expires_at,
          rewardPoints: session.reward_points
        },
        replace: true
      });
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleExitChallenge = () => {
    navigate(`/practice/song/${song?.id}`, {
      state: {},
      replace: true
    });
  };

  const loadSong = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('stripeensongs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) {
        toast.error('Song not found');
        navigate('/practice');
        return;
      }

      setSong(data);
    } catch (error: any) {
      toast.error('Error loading song');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const loadUserStats = async () => {
    if (!user) return;

    try {
      // Load latest stat for this song
      const { data: stat, error } = await supabase
        .from('stripepracticestats')
        .select('*')
        .eq('user_id', user.id)
        .eq('song_id', id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // returns null if none, one object if exists

      if (error) console.error(error);
      setStat(stat ?? null);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const handleExerciseComplete = async (exerciseType: ExerciseType, results: any) => {
    if (!user || !song) return;

    try {
      // Save exercise results
      const { error } = await supabase
        .from('stripepracticeresults')
        .insert({
          user_id: user.id,
          song_id: song.id,
          exercise_type: exerciseType,
          score: results.score,
          total_questions: results.totalQuestions,
          time_taken: results.timeTaken,
          answers: results.answers
        });

      if (error) throw error;

      // Update local results
      setExerciseResults(prev => ({
        ...prev,
        [exerciseType]: results
      }));

      // Update practice stats
      await updatePracticeStats(results.score, results.totalQuestions);

      // Show celebration for good scores
      if (results.score / results.totalQuestions >= 0.8) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      }

      toast.success(`¡Excelente! Puntuación: ${results.score}/${results.totalQuestions}`);
    } catch (error) {
      console.error('Error saving exercise results:', error);
      toast.error('Failed to save exercise results');
    }
  };

  const updatePracticeStats = async (score: number, totalQuestions: number) => {
    if (!user) return;

    try {
      const accuracy = (score / totalQuestions) * 100;
      
      if (!song) return;

      // example: find existing stat for this user+song and update, otherwise insert
      const { data: existing, error: selErr } = await supabase
        .from('stripepracticestats')
        .select('*')
        .eq('user_id', user.id)
        .eq('song_id', song.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (selErr) { console.error(selErr); }

      if (existing) {
        await supabase
          .from('stripepracticestats')
          .update({
            total_exercises: existing.total_exercises + 1,
            completed_exercises: existing.completed_exercises + 1,
            accuracy_rate: Math.round(((existing.accuracy_rate * existing.total_exercises) + accuracy) / (existing.total_exercises + 1))
          })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('stripepracticestats')
          .insert({
            user_id: user.id,
            song_id: song.id,
            total_exercises: 1,
            completed_exercises: 1,
            accuracy_rate: Math.round(accuracy),
            favorite_exercise: selectedExercise
          });
      }
    } catch (error) {
      console.error('Error updating practice stats:', error);
    }
  };

  const exerciseTypes = [
    {
      id: 'fill-blanks' as ExerciseType,
      title: 'Completar',
      icon: '✏️',
      color: 'from-yellow-400 to-yellow-600',
      description: 'Espacios'
    },
    {
      id: 'listen-write' as ExerciseType,
      title: 'Escuchar',
      icon: '🎧',
      color: 'from-yellow-400 to-yellow-600',
      description: 'Y escribir'
    },
    {
      id: 'verb-conjugation' as ExerciseType,
      title: 'Verbos',
      icon: '📝',
      color: 'from-yellow-400 to-yellow-600',
      description: 'Conjugar'
    },
    {
      id: 'vocabulary' as ExerciseType,
      title: 'Vocabulario',
      icon: '📚',
      color: 'from-yellow-400 to-yellow-600',
      description: 'Quiz'
    }
  ];

  const renderExercise = () => {
    if (!song) return null;

    const commonProps: any = {
      song,
      onComplete: (results: any) => handleExerciseComplete(selectedExercise, results),
      challengeMode: isChallengeMode,
      sessionId,
      expiresAt,
      rewardPoints,
      onExitChallenge: handleExitChallenge
    };

    // For challenge mode, only show fill-blanks exercise
    if (isChallengeMode && sessionId && expiresAt) {
      return (
        <ChallengeFillBlanksExercise
          song={song}
          sessionId={sessionId}
          expiresAt={expiresAt}
          rewardPoints={rewardPoints}
          onComplete={(results: any) => handleExerciseComplete('fill-blanks', results)}
          onExit={handleExitChallenge}
        />
      );
    }

    switch (selectedExercise) {
      case 'fill-blanks':
        return <FillBlanksExercise {...commonProps} />;
      case 'listen-write':
        return <ListenWriteExercise {...commonProps} />;
      case 'verb-conjugation':
        return <VerbConjugationExercise {...commonProps} />;
      case 'vocabulary':
        return <VocabularyQuizExercise {...commonProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        {/* Starry Night Background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(200)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            ></div>
          ))}
        </div>
        
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Cargando ejercicio...</p>
        </div>
      </div>
    );
  }

  if (!song) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Starry Night Sky Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Stars */}
        {[...Array(300)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 4 + 1}px`,
              height: `${Math.random() * 4 + 1}px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 4}s`
            }}
          ></div>
        ))}
        
        {/* Shooting Stars */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-shooting-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 50}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: '3s'
            }}
          ></div>
        ))}

        {/* Moon */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-yellow-100 rounded-full shadow-lg animate-pulse opacity-80"></div>
        
        {/* Constellation Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30">
          <defs>
            <pattern id="constellation" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
              <line x1="50" y1="50" x2="150" y2="100" stroke="white" strokeWidth="0.5" opacity="0.3"/>
              <line x1="150" y1="100" x2="100" y2="150" stroke="white" strokeWidth="0.5" opacity="0.3"/>
              <circle cx="50" cy="50" r="1" fill="white"/>
              <circle cx="150" cy="100" r="1" fill="white"/>
              <circle cx="100" cy="150" r="1" fill="white"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#constellation)"/>
        </svg>
      </div>

      {/* Header with Back Button and Stats */}
      <div className="relative z-10 p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/practice')}
            className="flex items-center px-4 py-2 bg-white/10 backdrop-blur-lg rounded-full text-white hover:bg-white/20 transition-all duration-300 border border-white/20"
          >
            <FaArrowLeft className="w-4 h-4 mr-2" />
            <span className="font-medium">Volver</span>
          </button>

          {/* Stats Display */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center px-4 py-2 bg-yellow-400/20 backdrop-blur-lg rounded-full border border-yellow-400/30">
              <FaFire className="w-4 h-4 text-yellow-400 mr-2" />
              <span className="text-white font-bold">{streak}</span>
            </div>
            <div className="flex items-center px-4 py-2 bg-yellow-400/20 backdrop-blur-lg rounded-full border border-yellow-400/30">
              <FaTrophy className="w-4 h-4 text-yellow-400 mr-2" />
              <span className="text-white font-bold">{xp} XP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Exercise Card */}
      <div className="relative z-10 px-6 pb-32">
        <div className="max-w-4xl mx-auto">
          {/* Glassmorphic Exercise Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden transform hover:scale-[1.01] transition-all duration-500">
            {/* Song Header */}
            <div className="p-6 bg-gradient-to-r from-black/30 to-transparent border-b border-white/10">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={song.song_cover}
                    alt={song.creator}
                    className="w-16 h-16 rounded-2xl object-cover shadow-lg"
                  />
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                    <FaPlay className="w-3 h-3 text-black" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{song.creator}</h2>
                  <p className="text-white/70 capitalize font-medium">{song.language}</p>
                </div>
              </div>
            </div>

            {/* Exercise Content */}
            <div className="relative">
              {renderExercise()}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Exercise Menu (Sticky) - CENTERED */}
      <div className={`fixed bottom-0 left-0 right-0 z-20 p-2 sm:p-3 ${isChallengeMode ? 'hidden' : ''}`}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-black/60 backdrop-blur-xl rounded-xl border border-yellow-400/30 p-2 shadow-2xl">
            {/* Scrollable Container with Hidden Scrollbar */}
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex items-center justify-center space-x-3 min-w-max px-2">
                {exerciseTypes.map((exercise, index) => (
                  <React.Fragment key={exercise.id}>
                    <button
                      onClick={() => setSelectedExercise(exercise.id)}
                      className={`relative flex-shrink-0 px-3 py-2.5 rounded-lg transition-all duration-300 transform min-w-[80px] ${
                        selectedExercise === exercise.id
                          ? `bg-gradient-to-r ${exercise.color} scale-105 shadow-lg text-black`
                          : 'bg-white/10 hover:bg-white/20 hover:scale-102 text-white'
                      }`}
                    >
                      {/* Exercise Content */}
                      <div className="text-center">
                        <div className="text-lg mb-1">{exercise.icon}</div>
                        <div className={`font-semibold text-xs leading-tight ${
                          selectedExercise === exercise.id ? 'text-black' : 'text-white'
                        }`}>{exercise.title}</div>
                        <div className={`text-[10px] leading-tight ${
                          selectedExercise === exercise.id ? 'text-black/70' : 'text-white/70'
                        }`}>{exercise.description}</div>
                      </div>

                      {/* Completion Badge */}
                      {exerciseResults[exercise.id] && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                          <FaCheck className="w-2 h-2 text-black" />
                        </div>
                      )}

                      {/* Active Indicator */}
                      {selectedExercise === exercise.id && (
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-black rounded-full animate-pulse"></div>
                      )}
                    </button>
                    
                    {/* Separator Line */}
                    {index < exerciseTypes.length - 1 && (
                      <div className="w-px h-8 bg-yellow-400/30 flex-shrink-0"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Celebration Animation */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-6xl animate-bounce">🎉</div>
          </div>
          {/* Confetti */}
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 bg-yellow-400 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`
              }}
            ></div>
          ))}
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes shooting-star {
          0% { 
            transform: translateX(-100px) translateY(100px) rotate(-45deg);
            opacity: 0;
          }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { 
            transform: translateX(300px) translateY(-300px) rotate(-45deg);
            opacity: 0;
          }
        }
        
        @keyframes confetti {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
        
        .animate-shooting-star {
          animation: shooting-star 3s linear infinite;
        }
        
        .animate-confetti {
          animation: confetti 3s linear forwards;
        }

        /* Hide scrollbar but keep functionality */
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;  /* Chrome, Safari, Opera */
        }

        /* Smooth scrolling */
        .scrollbar-hide {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
}